// ============================================================================
// SignInPage — public attendance sign-in (Task #52).
// ============================================================================
// Routed via #signin/<event_id>/<day_index>. No auth required.
//
// Three steps:
//   1. Enter email → POST /api/signin/lookup → get candidates
//   2. (If multiple candidates) pick which person you are
//   3. Choose "Draw" or "Type" → produce signature → POST /api/signin/submit
//
// Drawn signatures are base64 data URLs (PNG); typed initials are plain text.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle, CheckCircle2, ArrowLeft, PenLine, Type } from 'lucide-react';
import { api, type SignInCandidate } from '../api';
import { SignaturePad, type SignaturePadHandle } from './SignaturePad';

interface Props {
  eventId: number;
  dayIndex: number;
}

type Step = 'email' | 'pick' | 'sign' | 'done' | 'already';

export function SignInPage({ eventId, dayIndex }: Props) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [candidates, setCandidates] = useState<SignInCandidate[]>([]);
  const [eventTitle, setEventTitle] = useState('');
  const [picked, setPicked] = useState<SignInCandidate | null>(null);
  const [mode, setMode] = useState<'draw' | 'type'>('draw');
  const [typedInitials, setTypedInitials] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const padRef = useRef<SignaturePadHandle>(null);

  useEffect(() => { setError(''); }, [step]);

  const doLookup = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!email.trim()) { setError('Please enter your email.'); return; }
    setBusy(true); setError('');
    try {
      const r = await api.signinLookup({ event_id: eventId, day_index: dayIndex, email: email.trim() });
      setCandidates(r.candidates);
      setEventTitle(r.event_title);
      // Pick the first not-yet-signed-in candidate as the default; if all are
      // already signed in, jump straight to the "already signed in" view.
      const remaining = r.candidates.filter((c) => !c.already_present);
      if (remaining.length === 0) {
        setStep('already');
      } else if (remaining.length === 1) {
        setPicked(remaining[0]);
        setStep('sign');
      } else {
        setStep('pick');
      }
    } catch (err: any) {
      setError(err?.message || 'Lookup failed.');
    } finally {
      setBusy(false);
    }
  };

  const doSubmit = async () => {
    if (!picked) return;
    let signature_data: string | null = null;
    let signature_typed: string | null = null;
    if (mode === 'draw') {
      const data = padRef.current?.getDataUrl() ?? null;
      if (!data) { setError('Please sign with your finger or mouse before submitting.'); return; }
      signature_data = data;
    } else {
      if (!typedInitials.trim()) { setError('Type your name or initials.'); return; }
      signature_typed = typedInitials.trim();
    }
    setBusy(true); setError('');
    try {
      await api.signinSubmit({
        attendee_id: picked.attendee_id,
        guest_id: picked.guest_id ?? null,
        day_index: dayIndex,
        signature_data,
        signature_typed,
        check_in_method: mode === 'draw' ? 'signature_drawn' : 'signature_typed',
      });
      setStep('done');
    } catch (err: any) {
      setError(err?.message || 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center">
          <a href="/" className="flex items-center gap-3" aria-label="Up in Farms — home">
            <img src="/logo.jpg" alt="Up in Farms Food Hub" className="h-12 md:h-14 w-auto" />
          </a>
          <div className="ml-auto text-xs text-gray-500">
            Day {dayIndex} sign-in
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-md mx-auto px-4 py-8 w-full">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">

          {step === 'email' && (
            <form onSubmit={doLookup} className="p-6 space-y-4">
              <div>
                <h1 className="text-xl font-bold text-uif-green-dark mb-1">Welcome — let's sign you in</h1>
                <p className="text-sm text-gray-600">Enter the email you used to register.</p>
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>{error}</div>
                </div>
              )}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                autoFocus
                autoComplete="email"
                className="w-full rounded-md border border-gray-300 px-4 py-3 text-base focus:border-uif-green focus:ring-2 focus:ring-uif-green focus:outline-none"
              />
              <button
                type="submit"
                disabled={busy || !email.trim()}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-uif-green text-white font-semibold px-4 py-3 text-base hover:bg-uif-green-dark disabled:bg-gray-300"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Find my registration'}
              </button>
            </form>
          )}

          {step === 'pick' && (
            <div className="p-6 space-y-4">
              <button type="button" onClick={() => setStep('email')} className="text-xs text-gray-600 inline-flex items-center gap-1">
                <ArrowLeft className="h-3 w-3" /> Back
              </button>
              <h1 className="text-lg font-semibold text-uif-green-dark">{eventTitle}</h1>
              <p className="text-sm text-gray-700">Multiple people are registered under this email. Who's signing in?</p>
              <div className="space-y-2">
                {candidates.map((c) => (
                  <button
                    key={`${c.attendee_id}-${c.guest_id ?? 'p'}`}
                    type="button"
                    disabled={c.already_present}
                    onClick={() => { setPicked(c); setStep('sign'); }}
                    className={
                      'w-full flex items-center justify-between rounded-md border px-4 py-3 text-left transition-colors ' +
                      (c.already_present
                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 hover:border-uif-green hover:bg-uif-green/5 text-gray-900')
                    }
                  >
                    <span className="font-medium">{c.full_name}</span>
                    {c.already_present && (
                      <span className="inline-flex items-center gap-1 text-xs text-uif-green-dark">
                        <CheckCircle2 className="h-3 w-3" /> already signed in
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 'sign' && picked && (
            <div className="p-6 space-y-4">
              <button type="button" onClick={() => candidates.length > 1 ? setStep('pick') : setStep('email')}
                className="text-xs text-gray-600 inline-flex items-center gap-1">
                <ArrowLeft className="h-3 w-3" /> Back
              </button>
              <div>
                <h1 className="text-lg font-semibold text-uif-green-dark">{eventTitle}</h1>
                <p className="text-sm text-gray-700">
                  Signing in as <span className="font-semibold">{picked.full_name}</span>.
                </p>
              </div>

              <div className="flex gap-1 bg-gray-100 rounded-md p-1">
                <button type="button" onClick={() => setMode('draw')}
                  className={
                    'flex-1 inline-flex items-center justify-center gap-1.5 rounded-md py-2 text-sm font-medium ' +
                    (mode === 'draw' ? 'bg-white shadow text-uif-green-dark' : 'text-gray-600')
                  }>
                  <PenLine className="h-4 w-4" /> Draw signature
                </button>
                <button type="button" onClick={() => setMode('type')}
                  className={
                    'flex-1 inline-flex items-center justify-center gap-1.5 rounded-md py-2 text-sm font-medium ' +
                    (mode === 'type' ? 'bg-white shadow text-uif-green-dark' : 'text-gray-600')
                  }>
                  <Type className="h-4 w-4" /> Type initials
                </button>
              </div>

              {mode === 'draw' ? (
                <SignaturePad ref={padRef} height={200} />
              ) : (
                <input
                  type="text"
                  value={typedInitials}
                  onChange={(e) => setTypedInitials(e.target.value)}
                  placeholder="Type your name or initials"
                  autoFocus
                  className="w-full rounded-md border border-gray-300 px-4 py-3 text-base focus:border-uif-green focus:ring-2 focus:ring-uif-green focus:outline-none"
                />
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>{error}</div>
                </div>
              )}

              <button
                type="button"
                onClick={doSubmit}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-uif-green text-white font-semibold px-4 py-3 text-base hover:bg-uif-green-dark disabled:bg-gray-300"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
                Sign me in
              </button>
            </div>
          )}

          {step === 'done' && (
            <div className="p-8 text-center space-y-3">
              <CheckCircle2 className="h-14 w-14 text-uif-green mx-auto" />
              <h1 className="text-xl font-bold text-uif-green-dark">You're signed in!</h1>
              <p className="text-sm text-gray-700">
                Thanks, {picked?.full_name.split(/\s+/)[0]}. Have a great session.
              </p>
              <p className="text-xs text-gray-500">
                You can close this tab. If you need to sign in someone else, use the same QR or link.
              </p>
            </div>
          )}

          {step === 'already' && (
            <div className="p-8 text-center space-y-3">
              <CheckCircle2 className="h-14 w-14 text-gray-400 mx-auto" />
              <h1 className="text-xl font-bold text-gray-800">Already signed in</h1>
              <p className="text-sm text-gray-700">
                Looks like everyone registered under this email is already checked in for Day {dayIndex} of <span className="font-semibold">{eventTitle}</span>.
              </p>
              <p className="text-xs text-gray-500">
                If that's wrong, find a UIF staff member at the venue and they can fix it.
              </p>
            </div>
          )}

        </div>
      </main>

      <footer className="border-t border-gray-200 bg-white py-6">
        <div className="max-w-2xl mx-auto px-4 text-center text-xs text-gray-500">
          Up in Farms LLC · <a href="mailto:support@upinfarms.com" className="text-uif-green hover:underline">support@upinfarms.com</a>
        </div>
      </footer>
    </div>
  );
}
